import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  LIMITE_LINHAS, NOME_TIPO, camposDo, excessoDeLinhas, limitesDaEntrada, mapearColunas,
  type CampoDef, type ColunaMapeada, type TipoImportacao,
} from "@shared/importacao";
import {
  abasLegiveis, baixarArquivo, desfechoDoErro, escolherAba, gerarModelo, guardarLotePendente, lerArquivo, limitesDaAba,
  limparLotePendente, loteDoConteudo, loteParaGravar, lotePendente, mensagemDeErro, montarEntrada, nomeArquivoModelo,
  nomeMes, type AbaLida,
} from "@/lib/importacaoPlanilha";

// Importar / exportar planilha de sugestões de tema e de cards (out/26, João).
// Só aparece para o time Raz; o servidor também recusa quem não é @razconsulting.

const TIPOS: { v: TipoImportacao; titulo: string; ajuda: string }[] = [
  { v: "temas", titulo: "Sugestões de tema", ajuda: "Planejamento. Tudo entra na coluna Sugestão de Tema." },
  { v: "pautas", titulo: "Cards", ajuda: "Pautas que já estão em alguma etapa do quadro." },
];
const GRUPOS: CampoDef["grupo"][] = ["Identificação", "Planejamento", "Classificação", "Conteúdo", "Peça relacionada", "Links"];

function padraoDo(tipo: TipoImportacao): Set<string> {
  return new Set(camposDo(tipo).filter(c => c.obrigatorio || c.padrao).map(c => c.key));
}
function plural(n: number, um: string, varios: string) { return `${n} ${n === 1 ? um : varios}`; }
function nomeItens(tipo: TipoImportacao, n: number) {
  return tipo === "temas" ? plural(n, "sugestão de tema", "sugestões de tema") : plural(n, "card", "cards");
}

export default function ImportarExportarModal({
  clientId, onClose, onImported,
}: { clientId: number; onClose: () => void; onImported: () => void }) {
  const { session } = useAuth();
  const [modo, setModo] = useState<"exportar" | "importar">("exportar");
  const [tipo, setTipo] = useState<TipoImportacao>("temas");
  const ctxQ = trpc.importacao.contexto.useQuery({ clientId }, { staleTime: 60_000 });
  const contexto = ctxQ.data;
  // Enquanto grava, a janela não fecha (nem pelo fundo, nem pelo Esc, nem pelo ×).
  const [ocupado, setOcupado] = useState(false);
  const ocupadoRef = useRef(false);
  ocupadoRef.current = ocupado;
  const fechar = useCallback(() => { if (!ocupadoRef.current) onClose(); }, [onClose]);
  // Fecha pelo fundo só se o clique começou e terminou no fundo (arrastar uma
  // seleção de texto para fora da janela não fecha).
  const cliqueNoFundo = useRef(false);

  // Esc fecha só esta janela
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); fechar(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [fechar]);

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-2 md:p-4"
      onMouseDown={e => { cliqueNoFundo.current = e.target === e.currentTarget; }}
      onClick={e => { if (cliqueNoFundo.current && e.target === e.currentTarget) fechar(); cliqueNoFundo.current = false; }}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[94vh] flex flex-col shadow-[var(--elev-3)]" onClick={e => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 border-b border-[#E7E5E0]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>Importar / exportar planilha</h3>
              <p className="text-xs text-zinc-500 mt-1">
                {contexto ? <>Cliente: <strong>{contexto.clienteNome}</strong>. </> : null}
                Só o time Raz vê esta função.
              </p>
            </div>
            <button type="button" onClick={fechar} disabled={ocupado} className="text-zinc-400 hover:text-zinc-600 text-xl leading-none px-1 disabled:opacity-30" aria-label="Fechar">×</button>
          </div>
          <div className="flex items-center gap-1 bg-[#F5F4F1] rounded-lg p-1 mt-4 w-fit">
            {(["exportar", "importar"] as const).map(m => (
              <button key={m} type="button" onClick={() => setModo(m)} disabled={ocupado}
                className="px-3 py-1.5 text-xs font-semibold rounded-md disabled:cursor-not-allowed"
                style={{ background: modo === m ? "#fff" : "transparent", color: modo === m ? "#0A0A0A" : "#71717A" }}>
                {m === "exportar" ? "Exportar modelo" : "Importar planilha"}
              </button>
            ))}
          </div>
        </div>

        {/* Só troca a tela pelo erro se não há dados: uma nova busca que falha não desmonta a importação. */}
        {ctxQ.error && !ctxQ.data ? (
          <p className="px-5 py-6 text-sm text-red-600">{ctxQ.error.message}</p>
        ) : modo === "exportar" ? (
          <Exportar clientId={clientId} tipo={tipo} setTipo={setTipo} contexto={contexto} email={session?.email ?? ""} />
        ) : (
          <Importar clientId={clientId} tipo={tipo} setTipo={setTipo} contexto={contexto} onImported={onImported} onClose={fechar} onOcupado={setOcupado} />
        )}
      </div>
    </div>,
    document.body,
  );
}

function EscolhaTipo({ tipo, setTipo, rotulo, disabled = false }: { tipo: TipoImportacao; setTipo: (t: TipoImportacao) => void; rotulo: string; disabled?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">{rotulo}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {TIPOS.map(t => (
          <label key={t.v} className={cn("flex items-start gap-2.5 p-3 rounded-xl border",
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
            tipo === t.v ? "border-[#0A0A0A] bg-[#FAFAF8]" : "border-[#E7E5E0] hover:bg-[#FAFAF8]")}>
            <input type="radio" className="mt-0.5 accent-[var(--brand)]" checked={tipo === t.v} disabled={disabled} onChange={() => setTipo(t.v)} />
            <span>
              <span className="block text-sm font-semibold text-[#0A0A0A]">{t.titulo}</span>
              <span className="block text-[11px] text-zinc-500">{t.ajuda}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

type Contexto = {
  clienteNome: string;
  quadro: any[];
  etapas: { id: string; label: string }[];
  etapaInicial: string;
  sugestoes: { canais: string[]; formatos: string[]; linhas: string[]; territorios: string[] };
  meses: { temas: string[]; pautas: string[] };
} | undefined;

// ─── Exportar modelo ───────────────────────────────────────────────────────

function Exportar({ clientId, tipo, setTipo, contexto, email }: {
  clientId: number; tipo: TipoImportacao; setTipo: (t: TipoImportacao) => void; contexto: Contexto; email: string;
}) {
  const utils = trpc.useUtils();
  const [marcados, setMarcados] = useState<Record<TipoImportacao, Set<string>>>(() => ({ temas: padraoDo("temas"), pautas: padraoDo("pautas") }));
  const [comExistentes, setComExistentes] = useState(false);
  const [mes, setMes] = useState("");
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState("");
  const campos = camposDo(tipo);
  const sel = marcados[tipo];
  const meses = contexto?.meses[tipo] ?? [];

  useEffect(() => {
    // mês padrão: o mês atual, se tiver itens; senão o último com itens
    if (!meses.length) { setMes(""); return; }
    const d = new Date();
    const atual = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setMes(meses.includes(atual) ? atual : meses[meses.length - 1]);
  }, [tipo, meses.join(",")]);

  function alterna(k: string) {
    setMarcados(m => { const s = new Set(m[tipo]); s.has(k) ? s.delete(k) : s.add(k); return { ...m, [tipo]: s }; });
  }
  function define(s: Set<string>) { setMarcados(m => ({ ...m, [tipo]: s })); }

  async function baixar() {
    if (!contexto || gerando) return;
    setErro(""); setGerando(true);
    try {
      let existentes: { mes: string; itens: Record<string, unknown>[] } | null = null;
      if (comExistentes && mes) {
        const itens = await utils.importacao.exportarMes.fetch({ clientId, tipo, mes });
        existentes = { mes, itens: itens as Record<string, unknown>[] };
      }
      const geradoEm = new Date();
      const bytes = gerarModelo({
        tipo, campos: campos.filter(c => sel.has(c.key)).map(c => c.key),
        clienteNome: contexto.clienteNome, quadro: contexto.quadro, sugestoes: contexto.sugestoes,
        existentes, geradoPor: email, geradoEm,
      });
      baixarArquivo(bytes, nomeArquivoModelo({ tipo, clienteNome: contexto.clienteNome, geradoEm }));
    } catch (e: any) {
      setErro(e?.message ?? "Não foi possível gerar o modelo.");
    } finally {
      setGerando(false);
    }
  }

  const qtd = campos.filter(c => sel.has(c.key) || c.obrigatorio).length;
  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <EscolhaTipo tipo={tipo} setTipo={setTipo} rotulo="O que a planilha vai subir" />

        <div>
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Campos do modelo ({qtd})</p>
            <div className="flex gap-3 text-xs">
              <button type="button" onClick={() => define(new Set(campos.map(c => c.key)))} className="font-semibold text-[var(--brand)] hover:underline">Marcar tudo</button>
              <button type="button" onClick={() => define(padraoDo(tipo))} className="font-semibold text-zinc-500 hover:underline">Voltar ao padrão</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {GRUPOS.map(g => {
              const itens = campos.filter(c => c.grupo === g);
              if (!itens.length) return null;
              return (
                <div key={g} className="mb-3">
                  <p className="text-[10px] font-bold text-[var(--brand)] uppercase tracking-widest mb-1">{g}</p>
                  {itens.map(c => (
                    <label key={c.key} className={cn("flex items-start gap-2.5 py-1", c.obrigatorio ? "cursor-not-allowed" : "cursor-pointer")}>
                      <input type="checkbox" className="mt-0.5 accent-[var(--brand)]" disabled={c.obrigatorio}
                        checked={c.obrigatorio || sel.has(c.key)} onChange={() => alterna(c.key)} />
                      <span className="min-w-0">
                        <span className="text-sm text-[#0A0A0A]">{c.label}</span>
                        {c.obrigatorio && <span className="ml-1.5 text-[10px] font-semibold text-zinc-500 uppercase">obrigatório</span>}
                        <span className="block text-[11px] text-zinc-500 leading-snug">{c.ajuda}</span>
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-[#E7E5E0] p-3">
          <label className={cn("flex items-center gap-2.5 text-sm", meses.length ? "cursor-pointer" : "opacity-50 cursor-not-allowed")}>
            <input type="checkbox" className="accent-[var(--brand)]" disabled={!meses.length}
              checked={comExistentes && !!meses.length} onChange={e => setComExistentes(e.target.checked)} />
            Incluir o que já existe de
            <select value={mes} onChange={e => setMes(e.target.value)} disabled={!meses.length}
              className="border border-[#E7E5E0] rounded-lg px-2 py-1 text-sm bg-white">
              {meses.map(m => <option key={m} value={m}>{nomeMes(m)}</option>)}
            </select>
          </label>
          <p className="text-[11px] text-zinc-500 mt-1.5">
            {meses.length
              ? `Vai numa aba à parte, "Já no sistema", só para consulta. A importação não lê essa aba.`
              : `Este cliente ainda não tem ${tipo === "temas" ? "sugestão de tema" : "card"} com data.`}
          </p>
        </div>

        <div className="text-[11px] text-zinc-500 space-y-1">
          <p>O arquivo traz uma aba de instruções com as regras deste cliente{tipo === "pautas" && contexto ? ` (etapas: ${contexto.etapas.map(e => e.label).join(", ")})` : ""}, listas suspensas e uma linha de exemplo para apagar.</p>
        </div>
      </div>

      <div className="px-5 py-3 border-t border-[#E7E5E0] flex items-center justify-end gap-3 flex-wrap">
        {erro && <p className="text-xs text-red-600 mr-auto">{erro}</p>}
        <button type="button" onClick={baixar} disabled={!contexto || gerando}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--brand)" }}>
          {gerando ? "Gerando…" : "Baixar modelo (.xlsx)"}
        </button>
      </div>
    </>
  );
}

// ─── Importar planilha ─────────────────────────────────────────────────────

type Previa = {
  tipo: TipoImportacao;
  colunas: { indice: number; cabecalho: string; campo: string | null; campoLabel: string | null; repetida: boolean; destino: "campo" | "observacoes" | "ignorada" }[];
  linhas: {
    n: number; codigo: string | null; titulo: string; data: string | null; etapa: string | null; mae: string | null;
    maeOrigem: "planilha" | "sistema" | null; status: "ok" | "erro" | "ignorada"; erros: string[]; avisos: string[];
    extras: string[]; links: { campo: string; url: string }[];
  }[];
  errosGerais: string[]; avisosGerais: string[];
  total: number; validas: number; comErro: number; ignoradas: number; comVinculo: number;
  etapaInicial: string | null; podeGravar: boolean;
  loteGravado: { lote: string; em: number | null; autor: string | null; criados: number } | null;
};
type Resumo = {
  tipo: TipoImportacao; criados: number; comVinculo: number;
  ignorados: { linha: number; codigo: string | null; titulo: string; motivos: string[] }[]; ignoradosQtd: number;
  repetido?: boolean; gravadoEm?: number; gravadoPor?: string;
};

const MSG_SEM_RESPOSTA = 'Não deu para confirmar se gravou. A prévia abaixo foi refeita: se a gravação entrou, aparece o aviso azul com "Ver o que foi gravado". Se não aparecer, clique de novo no botão escuro "Gravar": ele reenvia a mesma gravação e não duplica.';

// Lista de colunas com o botão de ignorar (na prévia e quando só o limite de linhas barra).
function ColunasChips({ colunas, onAlternar, disabled }: { colunas: ColunaMapeada[]; onAlternar: (i: number) => void; disabled: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {colunas.map(c => {
        const ign = c.destino === "ignorada";
        return (
          <button key={c.indice} type="button" onClick={() => onAlternar(c.indice)} disabled={disabled}
            title={ign ? "Clique para voltar a usar esta coluna" : "Clique para ignorar esta coluna"}
            className={cn("text-left text-[11px] px-2 py-1 rounded-lg border disabled:cursor-not-allowed",
              ign ? "border-dashed border-zinc-300 text-zinc-400 line-through" :
              c.destino === "campo" ? "border-[#E7E5E0] text-[#0A0A0A] bg-[#FAFAF8]" : "border-[#FEDF89] text-[#93370D] bg-[#FFFAEB]")}>
            <strong>{c.cabecalho}</strong>{" "}
            {ign ? "ignorada" : c.destino === "campo" ? `> ${c.campoLabel}` : "> Observações"}
          </button>
        );
      })}
    </div>
  );
}

function criadosTexto(tipo: TipoImportacao, n: number) {
  if (tipo === "temas") return n === 1 ? "1 sugestão de tema criada" : `${n} sugestões de tema criadas`;
  return n === 1 ? "1 card criado" : `${n} cards criados`;
}

function fmtData(iso: string | null) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}
function fmtQuando(ms?: number | null) {
  if (!ms) return "";
  const d = new Date(ms);
  const z = (n: number) => String(n).padStart(2, "0");
  return `${z(d.getDate())}/${z(d.getMonth() + 1)}/${d.getFullYear()} às ${z(d.getHours())}:${z(d.getMinutes())}`;
}
const EH_LINK = /^(https?:\/\/|mailto:)/i;

function Importar({ clientId, tipo, setTipo, contexto, onImported, onClose, onOcupado }: {
  clientId: number; tipo: TipoImportacao; setTipo: (t: TipoImportacao) => void; contexto: Contexto;
  onImported: () => void; onClose: () => void; onOcupado: (v: boolean) => void;
}) {
  const [arquivo, setArquivo] = useState<{ nome: string; abas: AbaLida[] } | null>(null);
  const [abaNome, setAbaNome] = useState("");
  const [ignorar, setIgnorar] = useState<number[]>([]);
  const [erroArquivo, setErroArquivo] = useState("");
  const [filtro, setFiltro] = useState<"todas" | "erro" | "ok">("todas");
  const [resumo, setResumo] = useState<Resumo | null>(null);
  // Sobe a cada prévia refeita sem mudança na planilha (ex.: depois de um conflito ao gravar).
  const [rodada, setRodada] = useState(0);
  // Enquanto grava, nada na tela muda a planilha (arquivo, tipo, aba, colunas).
  const [gravando, setGravando] = useState(false);
  const [erroGravar, setErroGravar] = useState("");
  const preview = trpc.importacao.preview.useMutation();
  const aplicar = trpc.importacao.aplicar.useMutation();
  const consultar = trpc.importacao.consultarLote.useMutation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const aba = arquivo?.abas.find(a => a.nome === abaNome) ?? null;
  const montada = useMemo(() => {
    if (!aba) return { entrada: null, erro: "" };
    try { return { entrada: montarEntrada(aba, tipo, ignorar), erro: "" }; }
    catch { return { entrada: null, erro: "Não consegui montar as linhas desta aba. Confira se o arquivo abre no Excel e tente de novo." }; }
  }, [aba, tipo, ignorar]);
  const entrada = montada.entrada;
  // Limites conferidos aqui, antes de mandar (mesma mensagem que o servidor daria).
  // Quando o único problema é a quantidade de linhas, a tela mostra as colunas
  // para ignorar (coluna ignorada não conta).
  const conferencia = useMemo(() => {
    const daAba = limitesDaAba(aba);
    if (montada.erro) return { limites: [...daAba, montada.erro], soExcesso: false };
    if (!entrada) return { limites: daAba, soExcesso: false };
    try {
      // Aba cortada (ou outro erro de limite junto): ignorar coluna não resolve e a
      // lista de colunas não aparece, então a dica de coluna também não.
      let daEntrada = limitesDaEntrada(entrada, { semDicaDeColuna: daAba.length > 0 });
      if (daEntrada.length > 1) daEntrada = limitesDaEntrada(entrada, { semDicaDeColuna: true });
      const excesso = excessoDeLinhas(entrada);
      return { limites: [...daAba, ...daEntrada], soExcesso: !!excesso && !daAba.length && daEntrada.length === 1 };
    } catch {
      return { limites: [...daAba, "Não consegui conferir o tamanho da planilha. Confira se o arquivo abre no Excel e tente de novo."], soExcesso: false };
    }
  }, [aba, entrada, montada.erro]);
  const limites = conferencia.limites;
  const colunasLocais = useMemo(() => {
    if (!conferencia.soExcesso || !entrada) return [];
    try { return mapearColunas(tipo, entrada.cabecalhos, ignorar); } catch { return []; }
  }, [conferencia.soExcesso, entrada, tipo, ignorar]);
  // Lote tirado do conteúdo: a mesma planilha dá o mesmo lote (reescolher o
  // arquivo, reabrir a janela ou recarregar não grava duas vezes). O lote em uso
  // fica guardado na aba do navegador até a resposta definitiva.
  const loteBase = useMemo(() => {
    if (!entrada) return null;
    try { return loteDoConteudo(clientId, entrada); } catch { return null; }
  }, [entrada, clientId]);
  const loteAtual = () => (loteBase ? lotePendente(loteBase) ?? loteBase : null);

  useEffect(() => { onOcupado(gravando); }, [gravando, onOcupado]);
  // Planilha mudou (coluna ignorada, aba, tipo): o erro e a promessa da gravação anterior não valem mais.
  useEffect(() => { setErroGravar(""); }, [entrada]);
  useEffect(() => () => onOcupado(false), [onOcupado]);

  // Toda mudança (arquivo, aba, tipo, coluna ignorada) gera a prévia de novo, no servidor.
  useEffect(() => {
    if (!entrada) return;
    const lote = loteAtual();
    if (limites.length || !lote) { preview.reset(); return; }
    preview.mutate({ clientId, ...entrada, ignorar, loteId: lote });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrada, clientId, rodada]);

  async function escolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || gravando) return;
    setErroArquivo(""); setErroGravar(""); setResumo(null); setIgnorar([]); preview.reset(); aplicar.reset();
    try {
      const abas = lerArquivo(await f.arrayBuffer(), f.name);
      const { aba: a, tipoSugerido } = escolherAba(abas, tipo);
      if (!a || !a.linhas.length) { setErroArquivo("Não achei dados nesta planilha."); setArquivo(null); return; }
      if (tipoSugerido) setTipo(tipoSugerido);
      setArquivo({ nome: f.name, abas });
      setAbaNome(a.nome);
    } catch {
      setErroArquivo("Não consegui ler o arquivo. Envie .xlsx, .xls ou .csv.");
      setArquivo(null);
    }
  }

  function alternaIgnorar(i: number) {
    if (gravando) return;
    setIgnorar(xs => xs.includes(i) ? xs.filter(x => x !== i) : [...xs, i]);
  }

  // "Ver o que foi gravado": só consulta o lote L que a prévia achou gravado. Nunca grava.
  async function verResultado(tipoLote: TipoImportacao, L: string) {
    if (gravando) return;
    setGravando(true); setErroGravar("");
    try {
      const r = await consultar.mutateAsync({ clientId, tipo: tipoLote, loteId: L });
      if (!r) { setErroGravar("Não encontrei essa gravação. A prévia foi refeita."); setRodada(x => x + 1); return; }
      if (loteBase && lotePendente(loteBase) === L) limparLotePendente(loteBase);
      setResumo(r as Resumo);
    } catch (e: any) {
      setErroGravar(mensagemDeErro(e, "Não consegui consultar a gravação. Tente de novo."));
    } finally {
      setGravando(false);
    }
  }

  // reimportar: "Gravar de novo mesmo assim", ação explícita. Se já há um lote
  // pendente (enviado sem confirmação) para esta planilha, ele é reaproveitado;
  // lote de reimportação novo só quando não há pendente.
  async function gravar(reimportar = false) {
    const p = preview.data as Previa | undefined;
    if (!p || !entrada || !loteBase || gravando) return;
    if (p.loteGravado && !reimportar) return verResultado(p.tipo, p.loteGravado.lote);
    if (!p.podeGravar) return;
    const pendente = lotePendente(loteBase);
    const nome = contexto?.clienteNome ?? "este cliente";
    const fora = p.comErro ? ` ${p.comErro} ${p.comErro === 1 ? "linha com erro fica" : "linhas com erro ficam"} de fora.` : "";
    if (reimportar && !pendente) {
      if (!window.confirm(`Esta planilha já foi gravada. Gravar de novo cria ${nomeItens(p.tipo, p.validas)} outra vez em ${nome}.${fora} Continuar?`)) return;
    } else if (reimportar) {
      if (!window.confirm(`Há uma gravação desta planilha ainda sem confirmação. Reenviar a mesma gravação? Se ela já entrou, nada duplica.`)) return;
    } else if (!window.confirm(`Gravar ${nomeItens(p.tipo, p.validas)} em ${nome}?${fora}`)) return;
    const lote = loteParaGravar(loteBase, reimportar);
    guardarLotePendente(loteBase, lote);
    setGravando(true); setErroGravar("");
    // Sem certeza do resultado: volta para a prévia refeita (ela mostra se a gravação entrou)
    // e o lote pendente continua o mesmo.
    const incerto = (msg: string) => { setErroGravar(msg); setResumo(null); setRodada(x => x + 1); };
    try {
      const r = await aplicar.mutateAsync({
        clientId, ...entrada, ignorar, loteId: lote,
        confirmadas: p.linhas.filter(l => l.status === "ok").map(l => l.n),
      });
      limparLotePendente(loteBase);
      setResumo(r as Resumo);
      onImported();
    } catch (e: any) {
      const desfecho = desfechoDoErro(e);
      if (desfecho === "tentar_de_novo") {
        // Nada gravado (trava demorou): mesmo lote, tentar de novo não duplica.
        setErroGravar(mensagemDeErro(e, "Tente de novo em alguns segundos."));
      } else if (desfecho === "nada_gravado") {
        // Certeza de que nada foi gravado: solta o lote e refaz a prévia.
        limparLotePendente(loteBase);
        setErroGravar(mensagemDeErro(e, "Não foi possível gravar."));
        setResumo(null);
        setRodada(x => x + 1);
      } else {
        // Sem resposta, ou erro do banco: o lote fica o mesmo e a prévia mostra se entrou.
        incerto(e?.data?.code ? mensagemDeErro(e, MSG_SEM_RESPOSTA) : MSG_SEM_RESPOSTA);
      }
    } finally {
      setGravando(false);
    }
  }

  const p = preview.data as Previa | undefined;
  const abasLidas = arquivo ? abasLegiveis(arquivo.abas) : [];

  if (resumo) {
    return (
      <>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          <div className="rounded-xl p-4" style={{ background: "#ECFDF3", border: "1px solid #ABEFC6" }}>
            {resumo.repetido ? (
              <>
                <p className="text-sm font-semibold text-[#067647]">Esta planilha já tinha sido gravada.</p>
                <p className="text-xs text-[#067647] mt-1">
                  {resumo.gravadoEm ? `Gravada em ${fmtQuando(resumo.gravadoEm)}` : "Gravada antes"}{resumo.gravadoPor ? ` por ${resumo.gravadoPor}` : ""}. Nada foi gravado de novo.
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-[#067647]">Importação concluída.</p>
            )}
            <p className="text-sm text-[#067647] mt-1">
              {criadosTexto(resumo.tipo, resumo.criados)}
              {resumo.comVinculo ? `, ${resumo.comVinculo} com vínculo à peça-mãe` : ""}.
              {resumo.tipo === "temas" ? " Estão na coluna Sugestão de Tema." : ""}
            </p>
          </div>
          {resumo.ignorados.length > 0 ? (
            <div>
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Ficaram de fora ({resumo.ignorados.length})</p>
              <ul className="space-y-1.5">
                {resumo.ignorados.map(i => (
                  <li key={i.linha} className="text-xs text-zinc-700">
                    <strong>Linha {i.linha}</strong>{i.codigo ? ` · ${i.codigo}` : ""}{i.titulo ? ` · ${i.titulo.slice(0, 80)}` : ""}: {i.motivos.join(" ")}
                  </li>
                ))}
              </ul>
            </div>
          ) : resumo.ignoradosQtd > 0 ? (
            <p className="text-xs text-zinc-600">{resumo.ignoradosQtd === 1 ? "1 linha ficou de fora" : `${resumo.ignoradosQtd} linhas ficaram de fora`} naquela gravação.</p>
          ) : null}
          {erroGravar && <p className="text-xs text-red-600">{erroGravar}</p>}
        </div>
        <div className="px-5 py-3 border-t border-[#E7E5E0] flex justify-end gap-2 flex-wrap">
          {resumo.repetido && p?.podeGravar && (
            <button type="button" onClick={() => gravar(true)} disabled={gravando}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1] disabled:opacity-50 mr-auto">
              {gravando ? "Gravando…" : "Gravar de novo mesmo assim"}
            </button>
          )}
          <button type="button" disabled={gravando} onClick={() => { setResumo(null); setArquivo(null); setErroGravar(""); preview.reset(); aplicar.reset(); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-600 hover:bg-[#F5F4F1] disabled:opacity-50">Importar outra planilha</button>
          <button type="button" onClick={onClose} disabled={gravando} className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--brand)" }}>Fechar</button>
        </div>
      </>
    );
  }

  const linhasVisiveis = (p?.linhas ?? []).filter(l => filtro === "todas" || (filtro === "erro" ? l.status === "erro" : l.status === "ok"));
  const podeClicarGravar = !!p && (p.podeGravar || !!p.loteGravado) && !preview.isPending && !gravando && !limites.length;
  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <EscolhaTipo tipo={tipo} setTipo={setTipo} rotulo="O que a planilha traz" disabled={gravando} />

        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={gravando}
            className="px-4 py-2 rounded-lg text-sm font-semibold border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F4F1] disabled:opacity-40 disabled:cursor-not-allowed">
            {arquivo ? "Trocar arquivo" : "Escolher arquivo"}
          </button>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={escolherArquivo} disabled={gravando} />
          <span className="text-xs text-zinc-500 min-w-0 truncate">
            {arquivo ? arquivo.nome : `.xlsx ou .csv, até ${LIMITE_LINHAS} linhas. Use o modelo da aba ao lado.`}
          </span>
          {abasLidas.length > 1 && (
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              Aba:
              <select value={abaNome} disabled={gravando} onChange={e => { setAbaNome(e.target.value); setIgnorar([]); }}
                className="border border-[#E7E5E0] rounded-lg px-2 py-1 text-xs bg-white disabled:opacity-50">
                {abasLidas.map(a => <option key={a.nome} value={a.nome}>{a.nome}</option>)}
              </select>
            </label>
          )}
        </div>
        {erroArquivo && <p className="text-xs text-red-600">{erroArquivo}</p>}
        {limites.length > 0 && (
          <div className="rounded-xl p-3 text-xs text-[#B42318]" style={{ background: "#FEF3F2", border: "1px solid #FECDCA" }}>
            {limites.map((e, i) => <p key={i}>{e}</p>)}
          </div>
        )}
        {conferencia.soExcesso && colunasLocais.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Colunas da planilha</p>
            <ColunasChips colunas={colunasLocais} onAlternar={alternaIgnorar} disabled={gravando} />
            <p className="text-[11px] text-zinc-500 mt-1.5">Clique numa coluna para ignorar. Linha que só tem valor em coluna ignorada não conta.</p>
          </div>
        )}
        {preview.error && <p className="text-xs text-red-600">{mensagemDeErro(preview.error, "Não consegui conferir a planilha.")}</p>}
        {preview.isPending && <p className="text-xs text-zinc-500">Conferindo a planilha…</p>}

        {p && !preview.isPending && !limites.length && (
          <>
            {p.loteGravado && (
              <div className="rounded-xl p-3 text-xs text-[#175CD3]" style={{ background: "#EFF8FF", border: "1px solid #B2DDFF" }}>
                <p>
                  Esta planilha já foi gravada{p.loteGravado.em ? ` em ${fmtQuando(p.loteGravado.em)}` : ""}{p.loteGravado.autor ? ` por ${p.loteGravado.autor}` : ""} ({criadosTexto(p.tipo, p.loteGravado.criados)}).
                  Clicar em "Ver o que foi gravado" mostra o resultado daquela vez, sem gravar de novo.
                </p>
                {p.podeGravar && <p className="mt-1">Para criar tudo outra vez, use "Gravar de novo mesmo assim".</p>}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { n: p.total, t: "linhas lidas", c: "#0A0A0A" },
                { n: p.validas, t: "vão entrar", c: "#067647" },
                { n: p.comErro, t: "com erro", c: "#B42318" },
                { n: p.ignoradas, t: "ignoradas", c: "#71717A" },
                { n: p.comVinculo, t: "com vínculo", c: "#6941C6" },
              ].map(b => (
                <div key={b.t} className="rounded-xl border border-[#E7E5E0] px-3 py-2">
                  <p className="text-lg font-bold" style={{ color: b.c }}>{b.n}</p>
                  <p className="text-[11px] text-zinc-500">{b.t}</p>
                </div>
              ))}
            </div>

            {p.errosGerais.length > 0 && (
              <div className="rounded-xl p-3 text-xs text-[#B42318]" style={{ background: "#FEF3F2", border: "1px solid #FECDCA" }}>
                {p.errosGerais.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
            {p.avisosGerais.length > 0 && (
              <div className="rounded-xl p-3 text-xs text-[#93370D]" style={{ background: "#FFFAEB", border: "1px solid #FEDF89" }}>
                {p.avisosGerais.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}

            <div>
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Colunas da planilha</p>
              <ColunasChips colunas={p.colunas} onAlternar={alternaIgnorar} disabled={gravando} />
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Em amarelo, colunas que não são campo do Social Hub: entram em Observações como "Nome da coluna: valor". Clique numa coluna para ignorar ou voltar a usar.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1.5 text-xs">
                {([["todas", "Todas"], ["erro", `Com erro (${p.comErro})`], ["ok", `Vão entrar (${p.validas})`]] as const).map(([v, t]) => (
                  <button key={v} type="button" onClick={() => setFiltro(v)}
                    className={cn("px-2.5 py-1 rounded-md font-semibold", filtro === v ? "bg-[#0A0A0A] text-white" : "text-zinc-600 hover:bg-[#F5F4F1]")}>{t}</button>
                ))}
              </div>
              <div className="overflow-x-auto rounded-xl border border-[#E7E5E0]">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAF8] text-zinc-500">
                    <tr>
                      <th className="text-left font-semibold px-2 py-1.5">Linha</th>
                      <th className="text-left font-semibold px-2 py-1.5">Código</th>
                      <th className="text-left font-semibold px-2 py-1.5 min-w-[200px]">Título</th>
                      <th className="text-left font-semibold px-2 py-1.5">Data</th>
                      {p.tipo === "pautas" && <th className="text-left font-semibold px-2 py-1.5">Etapa</th>}
                      <th className="text-left font-semibold px-2 py-1.5">Peça-mãe</th>
                      <th className="text-left font-semibold px-2 py-1.5 min-w-[220px]">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasVisiveis.map(l => (
                      <tr key={l.n} className="border-t border-[#F0EEE9] align-top">
                        <td className="px-2 py-1.5 text-zinc-500">{l.n}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{l.codigo ?? ""}</td>
                        <td className="px-2 py-1.5 text-[#0A0A0A]">
                          {l.titulo || <span className="text-zinc-400">(sem título)</span>}
                          {l.status !== "ignorada" && (l.links ?? []).map((k, i) => (
                            <p key={`k${i}`} className="text-[11px] text-zinc-500 break-all mt-0.5">
                              {k.campo}:{" "}
                              {EH_LINK.test(k.url)
                                ? <a href={k.url} target="_blank" rel="noopener noreferrer" className="underline">{k.url}</a>
                                : k.url}
                            </p>
                          ))}
                          {l.extras.length > 0 && l.status !== "ignorada" && (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-[11px] text-[#93370D]">Vai para Observações ({l.extras.length})</summary>
                              <ul className="mt-1 space-y-0.5 text-[11px] text-zinc-600">
                                {l.extras.map((x, i) => <li key={i} className="break-words">{x.length > 240 ? `${x.slice(0, 240)}…` : x}</li>)}
                              </ul>
                            </details>
                          )}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtData(l.data)}</td>
                        {p.tipo === "pautas" && <td className="px-2 py-1.5 whitespace-nowrap">{l.etapa ?? ""}</td>}
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {l.mae ? <>{l.mae}{l.maeOrigem && <span className="text-zinc-400"> ({l.maeOrigem === "planilha" ? "nesta planilha" : "no sistema"})</span>}</> : ""}
                        </td>
                        <td className="px-2 py-1.5">
                          {l.status === "ok" && <span className="font-semibold text-[#067647]">Vai entrar</span>}
                          {l.status === "ignorada" && <span className="text-zinc-500">Ignorada</span>}
                          {l.erros.map((e, i) => <p key={`e${i}`} className="text-[#B42318]">{e}</p>)}
                          {l.avisos.map((e, i) => <p key={`a${i}`} className="text-[#93370D]">{e}</p>)}
                        </td>
                      </tr>
                    ))}
                    {!linhasVisiveis.length && (
                      <tr><td colSpan={7} className="px-2 py-4 text-center text-zinc-400">Nenhuma linha neste filtro.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="px-5 py-3 border-t border-[#E7E5E0]">
        {p && (
          <p className="text-[11px] text-zinc-500 mb-2">
            {p.tipo === "temas" ? "Tudo entra na coluna Sugestão de Tema. " : p.etapaInicial ? `Card sem etapa entra em ${p.etapaInicial}. ` : ""}
            Linhas com erro ficam de fora. A gravação é de uma vez só: se algo falhar, nada entra.
          </p>
        )}
        {erroGravar && <p className="text-xs text-red-600 mb-2">{erroGravar}</p>}
        <div className="flex justify-end gap-2 flex-wrap">
          <button type="button" onClick={onClose} disabled={gravando} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-600 hover:bg-[#F5F4F1] disabled:opacity-40">Cancelar</button>
          {p?.loteGravado && p.podeGravar && !(loteBase && lotePendente(loteBase)) && (
            <button type="button" onClick={() => gravar(true)} disabled={!podeClicarGravar}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1] disabled:opacity-50">
              Gravar de novo mesmo assim
            </button>
          )}
          <button type="button" onClick={() => gravar(false)} disabled={!podeClicarGravar}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--brand)" }}>
            {gravando ? "Gravando…" : p?.loteGravado ? "Ver o que foi gravado" : p && p.validas ? `Gravar ${nomeItens(p.tipo, p.validas)}` : "Gravar"}
          </button>
        </div>
      </div>
    </>
  );
}
